import { useCallback, useEffect, useMemo, useState } from "react";
import { App, Alert, Button, DatePicker, Flex, InputNumber, Select, Table, Tag, Tooltip, Typography } from "antd";
import { DownloadOutlined, ReloadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import {
  ApiKeyEvent,
  ApiKeyEventType,
  ApiKeyEventsFilter,
  useApiKeyEvents,
} from "../../context/apiKeyEventsContext";
import { useTranslation } from "react-i18next";

function fmt(value: string | null): string {
  return value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "-";
}

/** metadata オブジェクトを 1 行表示用に短く要約する。 */
function metadataExcerpt(metadata: Record<string, unknown> | null): string {
  if (!metadata) return "-";
  try {
    const s = JSON.stringify(metadata);
    return s.length > 80 ? s.slice(0, 80) + "…" : s;
  } catch {
    return "-";
  }
}

const EVENT_TYPES: ApiKeyEventType[] = ["issued", "rotated", "revoked", "expires_extended", "allowed_ips_changed"];

export default function EventsTab({ companyId }: { companyId: number | null }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { events, pagination, loading, refreshEvents, exportEventsCsv } = useApiKeyEvents();

  // フィルタ
  const [apiKeyId, setApiKeyId] = useState<number | null>(null);
  const [eventType, setEventType] = useState<ApiKeyEventType | null>(null);
  const [from, setFrom] = useState<dayjs.Dayjs | null>(null);
  const [to, setTo] = useState<dayjs.Dayjs | null>(null);
  const [exporting, setExporting] = useState(false);
  const eventTypeOptions = useMemo(
    () =>
      EVENT_TYPES.map((type) => ({
        label: t(`externalApiKey.events.types.${type}`),
        value: type,
      })),
    [t]
  );

  /** 現在のフィルタ状態をクエリ用 filter に変換する。 */
  const buildFilter = useCallback(
    (page: number, limit: number): ApiKeyEventsFilter => ({
      api_key_id: apiKeyId,
      event_type: eventType,
      from: from ? from.toISOString() : null,
      to: to ? to.toISOString() : null,
      page,
      limit,
    }),
    [apiKeyId, eventType, from, to]
  );

  // 企業選択時 / 企業変更時に初期ロード。
  // 企業が変わったらフィルタ (api_key_id 等) を必ずリセットしてから取得する。
  // 旧企業の api_key_id でフィルタしたまま別企業を読み込むと結果が誤って空になるため、
  // buildFilter(現在の state) ではなく明示的にクリアした filter で取得する。
  useEffect(() => {
    if (companyId != null) {
      setApiKeyId(null);
      setEventType(null);
      setFrom(null);
      setTo(null);
      refreshEvents(companyId, { api_key_id: null, event_type: null, from: null, to: null, page: 1, limit: 50 });
    }
  }, [companyId, refreshEvents]);

  const onSearch = useCallback(() => {
    if (companyId == null) return;
    refreshEvents(companyId, buildFilter(1, pagination.limit || 50));
  }, [companyId, refreshEvents, buildFilter, pagination.limit]);

  const onExport = useCallback(async () => {
    if (companyId == null) return;
    setExporting(true);
    try {
      const result = await exportEventsCsv(companyId, buildFilter(1, pagination.limit || 50));
      if (!result.ok) {
        message.error(result.message ?? t("externalApiKey.events.csvExportFailed"));
        return;
      }
      message.success(t("externalApiKey.events.csvDownloaded"));
    } finally {
      setExporting(false);
    }
  }, [companyId, exportEventsCsv, buildFilter, pagination.limit, message, t]);

  const columns: ColumnsType<ApiKeyEvent> = [
    { title: t("externalApiKey.events.columns.occurredAt"), dataIndex: "occurred_at", key: "occurred_at", render: fmt },
    {
      title: t("externalApiKey.events.columns.type"),
      dataIndex: "event_type",
      key: "event_type",
      render: (type: ApiKeyEventType) => <Tag>{t(`externalApiKey.events.types.${type}`)}</Tag>,
    },
    { title: t("externalApiKey.events.columns.key"), dataIndex: "key_name", key: "key_name", render: (v: string | null) => v ?? "-" },
    { title: t("externalApiKey.events.columns.keyPrefix"), dataIndex: "key_prefix", key: "key_prefix", render: (v) => (v ? <code>{v}</code> : "-") },
    {
      title: t("externalApiKey.events.columns.actor"),
      key: "actor",
      render: (_: unknown, row: ApiKeyEvent) =>
        `${row.actor_kind ?? "-"}${row.actor_admin_id != null ? ` (#${row.actor_admin_id})` : ""}`,
    },
    { title: t("externalApiKey.events.columns.reason"), dataIndex: "reason", key: "reason", render: (v: string | null) => v ?? "-" },
    {
      title: "metadata",
      dataIndex: "metadata",
      key: "metadata",
      render: (m: Record<string, unknown> | null) => (
        <Tooltip title={m ? JSON.stringify(m) : ""}>
          <span style={{ color: "#666" }}>{metadataExcerpt(m)}</span>
        </Tooltip>
      ),
    },
  ];

  if (companyId == null) {
    return <Alert type="info" showIcon message={t("externalApiKey.events.noCompany")} />;
  }

  return (
    <div>
      <Alert type="info" showIcon message={t("externalApiKey.events.retentionNote")} style={{ marginBottom: 16 }} />

      <Flex align="flex-end" gap="middle" wrap style={{ marginBottom: 16 }}>
        <div>
          <div style={{ marginBottom: 4 }}>{t("externalApiKey.events.apiKeyIdOptional")}</div>
          <InputNumber
            min={1}
            style={{ width: 140 }}
            value={apiKeyId ?? undefined}
            onChange={(v) => setApiKeyId(typeof v === "number" ? v : null)}
            placeholder={t("externalApiKey.events.apiKeyIdPlaceholder")}
          />
        </div>
        <div>
          <div style={{ marginBottom: 4 }}>{t("externalApiKey.events.eventTypeOptional")}</div>
          <Select
            allowClear
            style={{ width: 160 }}
            value={eventType ?? undefined}
            onChange={(v) => setEventType((v as ApiKeyEventType) ?? null)}
            options={eventTypeOptions}
            placeholder={t("common.all")}
          />
        </div>
        <div>
          <div style={{ marginBottom: 4 }}>{t("externalApiKey.events.fromOptional")}</div>
          <DatePicker showTime value={from} onChange={setFrom} placeholder={t("externalApiKey.events.startDateTime")} />
        </div>
        <div>
          <div style={{ marginBottom: 4 }}>{t("externalApiKey.events.toOptional")}</div>
          <DatePicker showTime value={to} onChange={setTo} placeholder={t("externalApiKey.events.endDateTime")} />
        </div>
        <Button type="primary" icon={<ReloadOutlined />} onClick={onSearch}>
          {t("common.search")}
        </Button>
        <Button icon={<DownloadOutlined />} loading={exporting} onClick={onExport}>
          {t("externalApiKey.events.csvExport")}
        </Button>
      </Flex>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={events}
        pagination={{
          current: pagination.page,
          pageSize: pagination.limit,
          total: pagination.total,
          onChange: (page, pageSize) => refreshEvents(companyId, buildFilter(page, pageSize)),
        }}
      />

      <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
        {t("externalApiKey.events.footerNote")}
      </Typography.Paragraph>
    </div>
  );
}
